/**
 * CrossShopView.jsx - Cross-Shop Stock View Component
 * 
 * Displays stock levels across all shops with filters and threshold management.
 * Extracted from StockRebalancer.jsx for maintainability.
 */
import React from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, Settings, Sliders } from "lucide-react";
import { STATUS_STYLES, STATUS_LABELS, SHOP_TYPE_STYLES } from "./constants";

/**
 * CrossShopView - Displays cross-shop stock levels in a table format
 * 
 * @param {Object} props
 * @param {Array} props.items - Cross-shop product items
 * @param {number} props.total - Total count of items (for pagination)
 * @param {boolean} props.loading - Loading state
 * @param {number} props.page - Current page number
 * @param {Function} props.onPageChange - Page change handler
 * @param {string} props.search - Search query
 * @param {Function} props.onSearchChange - Search change handler
 * @param {string} props.brand - Selected brand filter
 * @param {Function} props.onBrandChange - Brand filter change handler
 * @param {string} props.group - Selected category/group filter
 * @param {Function} props.onGroupChange - Group filter change handler
 * @param {string} props.status - Selected status filter
 * @param {Function} props.onStatusChange - Status filter change handler
 * @param {boolean} props.customOnly - Custom threshold only filter
 * @param {Function} props.onCustomOnlyChange - Custom only filter change handler
 * @param {Array} props.brandOptions - Available brand options
 * @param {Array} props.groupOptions - Available category/group options
 * @param {Array} props.shopsWithTypes - Shops list with type info
 * @param {boolean} props.isMasterOrSuper - User permission flag
 * @param {Function} props.onOpenUnifiedThresholdDialog - Opens unified threshold dialog
 * @param {Function} props.onOpenShopOverrideDialog - Opens shop override dialog
 * @param {Function} props.onOpenEditThreshold - Opens threshold edit dialog
 */
export default function CrossShopView({
  items = [],
  total = 0,
  loading = false,
  page = 1,
  onPageChange,
  search = "",
  onSearchChange,
  brand = "",
  onBrandChange,
  group = "",
  onGroupChange,
  status = "",
  onStatusChange,
  customOnly = false,
  onCustomOnlyChange,
  brandOptions = [],
  groupOptions = [],
  shopsWithTypes = [],
  isMasterOrSuper = false,
  onOpenUnifiedThresholdDialog,
  onOpenShopOverrideDialog,
  onOpenEditThreshold,
}) {
  const pageSize = 20;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card className="p-3">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Search Product</Label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or barcode..."
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-8 h-9"
                data-testid="crossshop-search-input"
              />
            </div>
          </div>
          <div className="w-[140px]">
            <Label className="text-xs">Brand</Label>
            <Select value={brand || "__all__"} onValueChange={(v) => onBrandChange(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="All Brands" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Brands</SelectItem>
                {brandOptions.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-[140px]">
            <Label className="text-xs">Category</Label>
            <Select value={group || "__all__"} onValueChange={(v) => onGroupChange(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Categories</SelectItem>
                {groupOptions.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-[140px]">
            <Label className="text-xs">Status</Label>
            <Select value={status || "__all__"} onValueChange={(v) => onStatusChange(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Status</SelectItem>
                <SelectItem value="low">Low Stock</SelectItem>
                <SelectItem value="optimal">Optimal</SelectItem>
                <SelectItem value="overstock">Overstock</SelectItem>
                <SelectItem value="imbalanced">Imbalanced</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="customOnly"
              checked={customOnly}
              onChange={(e) => onCustomOnlyChange(e.target.checked)}
              className="rounded"
              data-testid="crossshop-custom-only-checkbox"
            />
            <Label htmlFor="customOnly" className="text-xs cursor-pointer">Custom only</Label>
          </div>
        </div>
      </Card>

      {/* Cross-Shop Table */}
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[200px]">Product</TableHead>
                {shopsWithTypes.map(shop => (
                  <TableHead key={shop.name} className="text-center w-[110px]" data-testid={`shop-header-${shop.name}`}>
                    <div className="flex flex-col items-center">
                      <span>{shop.name}</span>
                      <Badge variant="outline" className={`text-[9px] mt-0.5 ${SHOP_TYPE_STYLES[shop.type || "normal"]}`}>
                        {shop.type === "warehouse" ? "WH" : "NOR"}
                      </Badge>
                    </div>
                  </TableHead>
                ))}
                <TableHead className="text-center w-[80px]">Total</TableHead>
                <TableHead className="text-center w-[120px]">Status</TableHead>
                <TableHead className="text-center w-[100px]">Threshold</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={shopsWithTypes.length + 4} className="text-center py-8 text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={shopsWithTypes.length + 4} className="text-center py-8 text-muted-foreground">
                    No products found
                  </TableCell>
                </TableRow>
              ) : (
                items.map(item => {
                  // Build a map of shop_name -> shop data for this item
                  const itemShopMap = {};
                  for (const shop of item.shops || []) {
                    itemShopMap[shop.shop_name] = shop;
                  }
                  
                  return (
                    <TableRow key={item.product_id} data-testid={`product-row-${item.product_id}`}>
                      <TableCell>
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="font-medium text-sm">{item.product_name}</div>
                            <div className="text-xs text-muted-foreground">{item.brand_name} · {item.group_name}</div>
                            {item.has_custom_threshold && (
                              <Badge variant="outline" className="text-[9px] mt-1 bg-indigo-50 text-indigo-700 border-indigo-200">
                                CUSTOM
                              </Badge>
                            )}
                          </div>
                          {/* Unified Manage Thresholds Button */}
                          {isMasterOrSuper && onOpenUnifiedThresholdDialog && (
                            <button
                              onClick={() => onOpenUnifiedThresholdDialog(item)}
                              className="flex-shrink-0 p-1.5 rounded hover:bg-purple-100 transition-colors"
                              title="Manage shop thresholds for this product"
                              data-testid={`manage-thresholds-btn-${item.product_id}`}
                            >
                              <Sliders className="w-4 h-4 text-purple-600" />
                            </button>
                          )}
                        </div>
                      </TableCell>
                      {/* Render cells in the same order as the header */}
                      {shopsWithTypes.map(headerShop => {
                        const shopData = itemShopMap[headerShop.name] || { quantity: 0, status: "LOW", min_qty: 0, max_qty: 0, threshold_source: "default" };
                        const thresholdSource = shopData.threshold_source || "default";
                        return (
                          <TableCell key={headerShop.name} className="text-center relative group" data-testid={`cell-${item.product_id}-${headerShop.name}`}>
                            <div className="font-mono text-sm">{shopData.quantity}</div>
                            <Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[shopData.status] || STATUS_STYLES.LOW}`}>
                              {shopData.status}
                            </Badge>
                            <div className="text-[9px] text-muted-foreground mt-0.5" data-testid={`cell-range-${item.product_id}-${headerShop.name}`}>
                              {shopData.min_qty}-{shopData.max_qty}
                            </div>
                            {/* Threshold source badge */}
                            <div className="mt-0.5">
                              {thresholdSource === "shop_override" ? (
                                <Badge variant="outline" className="text-[8px] bg-purple-100 text-purple-700 border-purple-200">
                                  SHOP
                                </Badge>
                              ) : thresholdSource === "custom" ? (
                                <Badge variant="outline" className="text-[8px] bg-indigo-50 text-indigo-600 border-indigo-200">
                                  CUSTOM
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-[8px] bg-gray-50 text-gray-500 border-gray-200">
                                  DEFAULT
                                </Badge>
                              )}
                            </div>
                            {/* Edit shop override button */}
                            {isMasterOrSuper && onOpenShopOverrideDialog && (
                              <button
                                onClick={() => onOpenShopOverrideDialog(item, shopData)}
                                className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded bg-white/80 hover:bg-purple-100"
                                title={`Set override for ${headerShop.name}`}
                                data-testid={`shop-override-btn-${item.product_id}-${headerShop.name}`}
                              >
                                <Settings className="w-3 h-3 text-purple-600" />
                              </button>
                            )}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-center font-mono font-semibold">
                        {item.total_stock}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className={STATUS_STYLES[item.overall_status]}>
                          {STATUS_LABELS[item.overall_status] || item.overall_status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {item.has_custom_threshold ? (
                          <div className="text-xs space-y-1">
                            {item.has_normal_custom && (
                              <div className="flex items-center justify-center gap-1">
                                <Badge variant="outline" className="text-[8px] bg-slate-100">NOR</Badge>
                                <span className="font-mono">{item.normal_min_qty}-{item.normal_max_qty}</span>
                              </div>
                            )}
                            {item.has_warehouse_custom && (
                              <div className="flex items-center justify-center gap-1">
                                <Badge variant="outline" className="text-[8px] bg-blue-100">WH</Badge>
                                <span className="font-mono">{item.warehouse_min_qty}-{item.warehouse_max_qty}</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">Default</span>
                        )}
                        {isMasterOrSuper && onOpenEditThreshold && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 mt-1"
                            onClick={() => onOpenEditThreshold(item)}
                            data-testid={`edit-threshold-crossshop-${item.product_id}`}
                          >
                            <Settings className="w-3 h-3" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        {/* Pagination */}
        {total > pageSize && (
          <div className="flex justify-between items-center p-3 border-t">
            <div className="text-xs text-muted-foreground">
              Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, total)} of {total}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(Math.max(1, page - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(page + 1)}
                disabled={page * pageSize >= total}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
